import { BasicFormState } from 'hooks/useForm';
import useGlobalSettings from 'hooks/useGlobalSettings';
import {
  AssignmentTypeEnum,
  CaseTypeEnum,
  CompanyModel,
  GlobalSettingEnum,
} from 'api/model';
import useMe from 'contexts/authentication/useMe';
import TextButton from 'components/inputs/TextButton';
import { AssignmentField, ExcludableKeyListRow } from './types';
import {
  Horizontal,
  InFormContainer,
  PaddedSpan,
  WarningDiv,
  WarningSpan,
} from './style';
import LabelWrap from 'components/inputs/LabelWrap';
import TooltipInfo from 'components/TooltipInfo';
import { ReactNode } from 'react';
import BasicEstimatedDurationPicker from '../CaseAssignments/BasicEstimatedDurationPicker';
import { formatDateTime } from 'utils/date-helpers';
import useEstimatedStopOverTimes from './useEstimatedStopOverTimes';
import { openGoogleMapsDirectionsArray } from 'utils/address-helper';
import useGetAssignmentAddresses from './useGetAssignmentAddresses';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';

const rowId = 'addPlannedFurtherTransportRow';

type Props = {
  assignmentId: number;
  currentCaseType: CaseTypeEnum;
  state: BasicFormState<AssignmentField>;
  canEdit: boolean;

  mkAddressInputFn: (
    name: AssignmentField,
    address: AssignmentField,
    city: AssignmentField,
    zip: AssignmentField,
    county: AssignmentField,
    label: ReactNode,
    filter?: (c: CompanyModel) => boolean,
    disabled?: boolean,
    warning?: string,
    extra?: React.ReactNode
  ) => ExcludableKeyListRow;

  onChange: (
    assignmentId: number,
    field: AssignmentField,
    value: string
  ) => void;
};

const useCaseAssignmentFormRow = ({
  assignmentId,
  currentCaseType,
  state,
  canEdit,
  onChange,
  mkAddressInputFn,
}: Props): ExcludableKeyListRow => {
  const loggedInUser = useMe();
  const { parsedData: globalSettings } = useGlobalSettings();
  const hasAccessToInEditStopOvers =
    globalSettings[GlobalSettingEnum.UsersWithAccessToInEditStopOvers]?.[
      loggedInUser?.userID ?? -1
    ] === true;

  const { estimatedStopOverStartTime, estimatedStopOverEndTime } =
    useEstimatedStopOverTimes(state);

  const { fromToStopOver } = useGetAssignmentAddresses(state);

  if (
    hasAccessToInEditStopOvers &&
    state[AssignmentField.addingStopOver] === 'false' &&
    assignmentId !== -1
  ) {
    const disable =
      state[AssignmentField.startTime] !== '' ||
      state[AssignmentField.endTime] !== '';
    return {
      exclude: () => false,
      key: 'Lägg till',
      id: rowId,
      value: (
        <TextButton
          disabled={disable}
          onClick={() =>
            onChange(assignmentId, AssignmentField.addingStopOver, 'true')
          }
        >
          {disable && (
            <TooltipInfo
              hoverable
              info={
                <div style={{ minWidth: 300, textAlign: 'left' }}>
                  Du kan bara skapa en planerad mellanlandning i det här steget
                  för ännu ej planerade uppdrag.
                </div>
              }
            />
          )}
          <FontAwesomeIcon icon={faPlus} /> Planerad mellanlandning
        </TextButton>
      ),
    };
  }

  return {
    exclude: () =>
      !hasAccessToInEditStopOvers ||
      state[AssignmentField.addingStopOver] !== 'true',
    key: <WarningSpan>Mellanlandning </WarningSpan>,
    id: rowId,
    value: (
      <InFormContainer tertiary>
        <LabelWrap label={<strong>Estimerad tid till mellanlandning</strong>}>
          <Horizontal style={{ alignItems: 'center' }}>
            <BasicEstimatedDurationPicker
              disabled={state[AssignmentField.estimatedDuration] !== ''}
              caseType={currentCaseType}
              assignmentType={AssignmentTypeEnum.PlannedFurtherTransport}
              addresses={fromToStopOver}
              value={
                state[AssignmentField.estimatedDuration]
                  ? Number(state[AssignmentField.estimatedDuration])
                  : undefined
              }
              hideMapsIcon
              onChange={(value) =>
                onChange(
                  assignmentId,
                  AssignmentField.estimatedDuration,
                  value.toString()
                )
              }
            />
            {state[AssignmentField.fixedAssignmentTimeInHours] !== '' && (
              <WarningDiv style={{ marginLeft: 5 }}>
                Fast tid: {state[AssignmentField.fixedAssignmentTimeInHours]} h
              </WarningDiv>
            )}

            {state[AssignmentField.estimatedStartTime] && (
              <PaddedSpan style={{ opacity: 0.4 }}>
                Estimerad starttid:
                {formatDateTime(
                  new Date(state[AssignmentField.estimatedStartTime])
                )}
              </PaddedSpan>
            )}
            {estimatedStopOverStartTime && (
              <PaddedSpan style={{ opacity: 0.4 }}>
                Beräknad sluttid:
                {formatDateTime(estimatedStopOverStartTime)}
              </PaddedSpan>
            )}
          </Horizontal>
        </LabelWrap>
        <LabelWrap
          label={
            <strong>
              Mellanlandningsadress{' '}
              {state[AssignmentField.addingStopOver] === 'true' && (
                <TextButton
                  onClick={() =>
                    onChange(
                      assignmentId,
                      AssignmentField.addingStopOver,
                      'false'
                    )
                  }
                >
                  <TooltipInfo
                    hoverable
                    info={
                      <div style={{ minWidth: 300, textAlign: 'left' }}>
                        Uppdraget kommer att delas upp med en planerad
                        vidaretransport. Detta går endast att göra när ingen FT
                        ännu är tilldelad Om du ångrar dig och vill ta bort
                        mellanlandningen igen, klicka på knappen{' '}
                        <em>Ångra mellanlandning</em>.
                      </div>
                    }
                  />
                  <FontAwesomeIcon icon={faMinus} /> Ångra mellanlandning
                </TextButton>
              )}
            </strong>
          }
        >
          <Horizontal>
            {
              mkAddressInputFn(
                AssignmentField.stopOverName,
                AssignmentField.stopOverAddress,
                AssignmentField.stopOverCity,
                AssignmentField.stopOverZip,
                AssignmentField.stopOverCounty,
                undefined,
                () => true,
                !canEdit,
                undefined
              ).value
            }
            <TextButton
              onClick={() => {
                openGoogleMapsDirectionsArray(fromToStopOver);
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              title="Öppna i Google Maps"
            >
              <img
                alt="Google maps icon"
                height={15}
                src="/images/googlemapsiconsmall.png"
                width={15}
              />
            </TextButton>
          </Horizontal>
        </LabelWrap>
        <LabelWrap label={<strong>Estimerad tid från mellanlandning</strong>}>
          <Horizontal style={{ alignItems: 'center' }}>
            <BasicEstimatedDurationPicker
              disabled={
                state[AssignmentField.stopOverFixedAssignmentTimeInHours] !== ''
              }
              caseType={currentCaseType}
              assignmentType={AssignmentTypeEnum.PlannedFurtherTransport}
              addresses={fromToStopOver}
              value={
                state[AssignmentField.stopOverEstimatedDuration]
                  ? Number(state[AssignmentField.stopOverEstimatedDuration])
                  : undefined
              }
              hideMapsIcon
              onChange={(value) =>
                onChange(
                  assignmentId,
                  AssignmentField.stopOverEstimatedDuration,
                  value.toString()
                )
              }
            />
            {state[AssignmentField.stopOverFixedAssignmentTimeInHours] !==
              '' && (
              <WarningDiv style={{ marginLeft: 5 }}>
                Fast tid:{' '}
                {state[AssignmentField.stopOverFixedAssignmentTimeInHours]} h
              </WarningDiv>
            )}

            {estimatedStopOverStartTime && (
              <PaddedSpan style={{ opacity: 0.4 }}>
                Beräknad starttid:
                {formatDateTime(estimatedStopOverStartTime)}
              </PaddedSpan>
            )}
            {estimatedStopOverEndTime && (
              <PaddedSpan style={{ opacity: 0.4 }}>
                Beräknad sluttid:
                {formatDateTime(estimatedStopOverEndTime)}
              </PaddedSpan>
            )}
          </Horizontal>
        </LabelWrap>
      </InFormContainer>
    ),
  };
};

export default useCaseAssignmentFormRow;
